/*------------------------------------------------------------------------------
  About      : Responsible for all navigation related activities
  
  Created on : Thu Jan 19 2023
  Author     : Siddharth Garg
  
  Copyright (c) 2023 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/

import { NavigateFunction }         from "react-router";
import { RunContextApp }            from "./RCApp"
import { Action, 
         Location,
       }                            from 'history'
import { ROUTE_TREE }               from "routes/AppRoutes";

export type LocationState = unknown;

export class UiRouter {

  private navigateFunc !: NavigateFunction
  private lastLocation !: Location

  constructor(private rc : RunContextApp) {
    window.addEventListener('popstate', this.onPopState.bind(this))
  }

  navigateToHome(navigate : NavigateFunction = this.navigateFunc) {
    
    if (process.env.REACT_APP_HOME_PAGE === 'CMS') {
      navigate(ROUTE_TREE.CMS.PROJECT_LIST.path, {
        state : {
          title : ROUTE_TREE.CMS.PROJECT_LIST.name,
          disableBack : true  
        }
      })
    } else {
      navigate(ROUTE_TREE.PROJECT.LIST.path, {
        state : {
          title : ROUTE_TREE.PROJECT.LIST.name,
          disableBack : true  
        }
      })  
    }
  }

  init(navigate : NavigateFunction) {

    if (!this.navigateFunc) {
      this.navigateFunc = navigate
    }
  }

  onLocationChange(location : Location) {
    this.rc.debug(this.rc.getName(this), 'Location change', location.pathname)
    this.lastLocation = location
  }

  goBack() {

    // Due to iframe issue
    if (this.lastLocation.pathname.startsWith('/cms/reco/edit/') || 
        this.lastLocation.pathname.startsWith('/cms/reco/create')) {
      this.navigate(ROUTE_TREE.CMS.RECO.LIST)
    } else {
      this.navigateFunc(-1)
    }
  }

  getLastLocation() {
    return this.lastLocation
  }

  navigate(route : {path : string, name : string}, locState : any = {}) {

    this.rc.debug(this.rc.getName(this), 'Navigating to', route.path, 'with title', 
      route.name, 'and locState', locState)

    let routePath = route.path

    if (route.path.includes('/:id')) {
      if (!locState.id) throw new Error('id is required inside locState for route with :id')
      routePath = routePath.replace(':id', locState.id)
    }

    // Check if we're already on the target route to prevent duplicate navigation
    const currentPath = window.location.pathname;
    if (currentPath === routePath && !locState.force) {
      this.rc.debug(this.rc.getName(this), 'Already on target route, skipping navigation');
      return;
    }

    this.navigateFunc(routePath, {
      replace : locState.replace || false,
      state : {
        title : locState.title || route.name,
        ...locState
      }
    })
  }

  // Force navigation even if already on the target route
  navigateForce(route : {path : string, name : string}, locState : any = {}) {
    this.navigate(route, { ...locState, force: true });
  }

  private onPopState(e : any) {
    this.rc.debug(this.rc.getName(this), 'onPopState', e)
  }

  private onNavEnd(location : Location, action : Action) {

    this.rc.debug(this.rc.getName(this), 'onNavEnd', location, action)
    this.rc.analytics.onNavChange(location, action)
  }
}
